"use client";

import React from "react";
import styles from "./blockchain-status.module.scss";
import { HealthResponse } from "@/types/health.types";
import { ServiceRowBadge, CountBadge } from "../components";
import { formatTimestamp, formatSecondsSince, truncateHash, truncateAddress, getChainName, getChainLogo, createScrollHandler } from "../utils";

interface BlockchainStatusProps {
  healthData: HealthResponse;
}

const BlockchainStatus: React.FC<BlockchainStatusProps> = ({ healthData }) => {


  // Check if rollup contracts are truly healthy (all events must be healthy)
  const getRollupContractsStatus = () => {
    if (!healthData.rollup) return "unhealthy";

    const rollupEvents = healthData.rollup.contracts.rollup?.events || [];
    const multiSigEvents = healthData.rollup.contracts.multiSig?.events || [];

    const allRollupEventsHealthy = rollupEvents.every(event => event.healthy);
    const allMultiSigEventsHealthy = multiSigEvents.every(event => event.healthy);

    return (allRollupEventsHealthy && allMultiSigEventsHealthy) ? "healthy" : "unhealthy";
  };

  // Check if rollup signers are truly healthy (all signers must be healthy)
  const getRollupSignersStatus = () => {
    if (!healthData.rollup || !healthData.rollup.signers || healthData.rollup.signers.length === 0) return "healthy"; // No signers means no problem

    const allSignersHealthy = healthData.rollup.signers.every(signer => signer.healthy);
    return allSignersHealthy ? "healthy" : "unhealthy";
  };

  // Overall rollup status
  const getRollupOverallStatus = () => {
    const contractsStatus = getRollupContractsStatus();
    const signersStatus = getRollupSignersStatus();

    return (contractsStatus === "healthy" && signersStatus === "healthy") ? "healthy" : "unhealthy";
  };



  const getBlockchainCounts = () => {
    const items: { healthy: boolean }[] = [];

    // Latest Block
    items.push({ healthy: healthData.latestBlock.healthy });

    // Ping
    items.push({ healthy: healthData.ping.healthy });

    // Rollup Contract Events
    if (healthData.rollup) {
      // Add rollup contract events
      if (healthData.rollup.contracts.rollup?.events) {
        healthData.rollup.contracts.rollup.events.forEach(event => {
          items.push({ healthy: event.healthy });
        });
      }

      // Add multiSig contract events if they exist
      if (healthData.rollup.contracts.multiSig?.events) {
        healthData.rollup.contracts.multiSig.events.forEach(event => {
          items.push({ healthy: event.healthy });
        });
      }

      // Rollup Signers - only if they exist
      if (healthData.rollup.signers && healthData.rollup.signers.length > 0) {
        healthData.rollup.signers.forEach(signer => {
          items.push({ healthy: signer.healthy });
        });
      }
    }

    const healthyCount = items.filter(item => item.healthy).length;
    const unhealthyCount = items.filter(item => !item.healthy).length;

    return { healthyCount, unhealthyCount };
  };

  const scrollToBlockchainContent = createScrollHandler('blockchain-content');

  return (
    <div id="blockchain-status" className={styles.card}>
      <div className={styles.header}>
        <h3>Blockchain Status</h3>
        <div className={styles.summary}>
          {getBlockchainCounts().healthyCount > 0 && (
            <CountBadge
              count={getBlockchainCounts().healthyCount}
              label="Healthy"
              isHealthy={true}
              onClick={scrollToBlockchainContent}
            />
          )}
          {getBlockchainCounts().unhealthyCount > 0 && (
            <CountBadge
              count={getBlockchainCounts().unhealthyCount}
              label="Unhealthy"
              isHealthy={false}
              onClick={scrollToBlockchainContent}
            />
          )}
        </div>
      </div>

      <div id="blockchain-content" className={styles.content}>
        {/* Latest Block Badge */}
        <ServiceRowBadge
          isHealthy={healthData.latestBlock.healthy}
          serviceName="latest block"
          chainId={healthData.latestBlock.chainId}
          metrics={[
            {
              label: `Block #${healthData.latestBlock.blockNumber.toLocaleString()}`,
              value: formatTimestamp(healthData.latestBlock.timestamp)
            }
          ]}
        />

        {/* Ping Badge */}
        <ServiceRowBadge
          isHealthy={healthData.ping.healthy}
          serviceName={`network ping ${truncateAddress(healthData.ping.contract)}`}
          chainId={healthData.ping.chainId}
          metrics={[
            {
              label: "Last Ping",
              value: formatSecondsSince(healthData.ping.timeSincePing)
            }
          ]}
        />

        {/* Rollup Contract Events */}
        {healthData.rollup && (
          <>
            {/* Rollup Contract Events */}
            {healthData.rollup.contracts.rollup.events.map((event, index) => (
              <ServiceRowBadge
                key={`rollup-${index}`}
                isHealthy={event.healthy}
                serviceName={`${healthData.rollup.contracts.rollup.contractName.toLowerCase()} ${truncateAddress(healthData.rollup.contracts.rollup.contractAddress)}`}
                chainId={11145513}
                subtitle={event.topicName}
                metrics={[
                  {
                    label: "Events",
                    value: event.eventsFound.toString()
                  }
                ]}
              />
            ))}

            {/* MultiSig Contract Events - only if they exist */}
            {healthData.rollup.contracts.multiSig?.events && healthData.rollup.contracts.multiSig.events.map((event, index) => (
              <ServiceRowBadge
                key={`multisig-${index}`}
                isHealthy={event.healthy}
                serviceName={`${healthData.rollup.contracts.multiSig.contractName.toLowerCase()} ${truncateAddress(healthData.rollup.contracts.multiSig.contractAddress)}`}
                chainId={11145513}
                subtitle={event.topicName}
                metrics={[
                  {
                    label: "Events",
                    value: event.eventsFound.toString()
                  }
                ]}
              />
            ))}
          </>
        )}

        {/* Rollup Signers */}
        {healthData.rollup && healthData.rollup.signers && healthData.rollup.signers.length > 0 && (
          <>
            {healthData.rollup.signers.map((signer, index) => (
              <ServiceRowBadge
                key={signer.address}
                isHealthy={signer.healthy}
                serviceName={`signer #${index + 1} ${truncateAddress(signer.address)}`}
                chainId={11145513}
                subtitle="rollup signer"
                metrics={[
                  {
                    label: "ETH",
                    value: signer.balance.toFixed(4)
                  }
                ]}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default BlockchainStatus;
